import { BILLING_PERIODS, PLANS, type BillingPeriodId, type PlanId } from './config';

/**
 * Shared pricing calculations (ADR-010). Exact integer arithmetic in minor units, using BigInt
 * internally so no intermediate product can lose precision:
 *
 *   period total       = monthly base × months × (1 − discount) × seats
 *   monthly equivalent = period total ÷ months  (÷ seats again for the per-person figure)
 *
 * Seats apply only to per-person plans. Discounts are taken once, from the undiscounted base.
 * Any result that is not a whole number of minor units is rounded half up to the nearest
 * hundredth; every currently configured price divides exactly.
 */

const BASIS = 10_000n;

/** Divide non-negative integers, rounding half up. */
function divideRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  return (numerator * 2n + denominator) / (denominator * 2n);
}

function toSafeNumber(value: bigint): number {
  if (value > BigInt(Number.MAX_SAFE_INTEGER)) throw new RangeError('Amount out of range');
  return Number(value);
}

export class InvalidSeatCountError extends Error {
  constructor() {
    super('Seat count must be a positive whole number');
    this.name = 'InvalidSeatCountError';
  }
}

function assertSeats(seats: number) {
  if (!Number.isSafeInteger(seats) || seats < 1) throw new InvalidSeatCountError();
}

export type Quote = {
  planId: PlanId;
  periodId: BillingPeriodId;
  /** Seats charged (always 1 for plans that are not per person). */
  seats: number;
  months: number;
  /** Amount payable for the whole billing period, all seats, in minor units. */
  periodTotalMinor: number;
  /** Period amount for one person (per-person plans) or the account. */
  periodTotalPerUnitMinor: number;
  /** Comparison figure: per month, per person for per-person plans. */
  monthlyEquivalentPerUnitMinor: number;
};

export function quote(planId: PlanId, periodId: BillingPeriodId, seats = 1): Quote {
  const plan = PLANS[planId];
  const period = BILLING_PERIODS[periodId];
  const chargedSeats = plan.perPerson ? seats : 1;
  assertSeats(chargedSeats);

  const months = BigInt(period.months);
  const discounted = BASIS - BigInt(period.discountBasisPoints);
  const perUnit = divideRoundHalfUp(BigInt(plan.monthlyBaseMinor) * months * discounted, BASIS);
  const total = perUnit * BigInt(chargedSeats);

  return {
    planId,
    periodId,
    seats: chargedSeats,
    months: period.months,
    periodTotalMinor: toSafeNumber(total),
    periodTotalPerUnitMinor: toSafeNumber(perUnit),
    monthlyEquivalentPerUnitMinor: toSafeNumber(divideRoundHalfUp(perUnit, months)),
  };
}

export type SeatParseResult = { ok: true; seats: number } | { ok: false; message: string };

/**
 * Parse a team-size entry. Accepts only positive whole numbers written as digits: rejects
 * empty, zero, negative, fractional, exponent and non-numeric input. No minimum team size or
 * volume discount is applied (none is approved).
 */
export function parseSeatCount(input: string): SeatParseResult {
  const text = input.trim();
  const message = 'Enter a whole number of people, 1 or more.';
  if (!/^\d+$/.test(text)) return { ok: false, message };
  const seats = Number(text);
  if (!Number.isSafeInteger(seats) || seats < 1) return { ok: false, message };
  return { ok: true, seats };
}

/**
 * Format minor units as SLE: whole leones without decimals ("SLE 2,550"), otherwise exactly
 * two decimals ("SLE 212.50"). Never rounds to whole leones.
 */
export function formatSLE(minor: number): string {
  if (!Number.isSafeInteger(minor) || minor < 0) throw new RangeError('Invalid amount');
  const whole = Math.floor(minor / 100);
  const cents = minor % 100;
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return cents === 0 ? `SLE ${grouped}` : `SLE ${grouped}.${cents.toString().padStart(2, '0')}`;
}
